import { useCallback, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import {
  DESIGN_CALENDAR_EVENT,
  fetchDesignCalendarAvisos,
  markDesignCalendarAvisoVisto,
} from "../../api/DesignCalendar/designCalendarApi";
import {
  DESIGN_CALENDAR_SEED_YEAR,
  designCalendarSeedAcciones,
} from "./designCalendarSeed2026";
import { todayIso, weekBounds } from "./designCalendarModel";
import {
  filterDesignCalendarAvisosLocal,
  markDesignCalendarVistoLocal,
} from "./designCalendarVistoLocal";
import { socket } from "../../socket";

function readUserId() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded.userId ?? decoded.id ?? null;
  } catch {
    return null;
  }
}

/** Fallback local: solo arranques (Por hacer), no notas del calendario. */
function avisosFromExcel(hoy = todayIso()) {
  if (!String(hoy).startsWith(`${DESIGN_CALENDAR_SEED_YEAR}-`)) return [];
  const bounds = weekBounds(hoy);
  if (!bounds) return [];
  const items = [];
  for (const accion of designCalendarSeedAcciones) {
    if (accion.Hecho) continue;
    if (accion.FechaAviso < bounds.start || accion.FechaAviso > bounds.end) {
      continue;
    }
    items.push({
      kind: "accion",
      id: accion.Id,
      fecha: accion.FechaAviso,
      texto: accion.Texto,
      simbolo: "inicio",
      simbolos: ["inicio"],
    });
  }
  items.sort((a, b) => a.fecha.localeCompare(b.fecha) || a.id - b.id);
  return filterDesignCalendarAvisosLocal(items, readUserId(), hoy);
}

export function useDesignCalendarAvisos(enabled = true) {
  const [items, setItems] = useState([]);
  const [usingLocal, setUsingLocal] = useState(false);

  const reload = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      setUsingLocal(false);
      return;
    }
    try {
      const data = await fetchDesignCalendarAvisos();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setUsingLocal(false);
    } catch (error) {
      console.error("Avisos Design Calendar:", error);
      setItems(avisosFromExcel());
      setUsingLocal(true);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      return undefined;
    }

    reload();

    let debounceTimer = null;
    const softReload = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        reload().catch(() => {});
      }, 280);
    };

    const onLocal = () => softReload();
    const onSocket = () => softReload();

    window.addEventListener(DESIGN_CALENDAR_EVENT, onLocal);
    window.addEventListener("focus", onLocal);
    socket.on("design-calendar-updated", onSocket);

    const pollId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      softReload();
    }, 25000);

    const onVisible = () => {
      if (document.visibilityState === "visible") softReload();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      window.clearInterval(pollId);
      window.removeEventListener(DESIGN_CALENDAR_EVENT, onLocal);
      window.removeEventListener("focus", onLocal);
      socket.off("design-calendar-updated", onSocket);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, reload]);

  const marcarVisto = useCallback(
    async (kind, id) => {
      if (usingLocal) {
        markDesignCalendarVistoLocal(readUserId(), kind, id);
        setItems((prev) => filterDesignCalendarAvisosLocal(prev, readUserId()));
        return;
      }
      await markDesignCalendarAvisoVisto(kind, id);
      setItems((prev) =>
        prev.filter(
          (item) => !(item.kind === kind && Number(item.id) === Number(id)),
        ),
      );
    },
    [usingLocal],
  );

  return { items, count: items.length, reload, marcarVisto };
}
